// StepOne.tsx
import {
    useCallback, useEffect, useMemo,
    useState,
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { Data } from 'widgets/WithdrawalPlan/types';
import { useTranslation } from 'react-i18next';
import { validateIban } from 'utils/validation';
import { useCreateCaseState } from './CreateCaseManager';
import { getBankInformationSchema } from './schemas/bankInformationSchema';
import { accountDetails, accountDetailsZurichDepots } from './constants';
import { BankInformationEnum, CaseBeneficiaryTypeEnum } from './CaseManagement.types';

const useBankInformation = () => {
    const { i18n: { language }, t } = useTranslation();

    const [continueButtonLoading, setContinueButtonLoading] = useState(false);

    const {
        saveCaseManagementData, beneficiaryInfoStatic, bankInformationData, bankInformationOptions, isKuDi, active3bPortfolios, hasAdditional3b, isActive3bPortfoliosLoading, isJA, isPOA, isPA, isPK, isAzp, beneficiaryInformationFromAPI, isPaymentOrderPrivateAccount, active3bPortfolioOptions,
    } = useCreateCaseState();

    const bankInfoSchema = useMemo(() => getBankInformationSchema({
        showBeneficiaryInfo: isKuDi, isJA, isPOA, isPA, isPK,
    }), [isKuDi, isJA, isPOA, isPA, isPK]);

    const schema: yup.ObjectSchema<Data> = useMemo(
        () => (yup.object({
            ...bankInfoSchema?.fields,
        })),
        [bankInfoSchema, getBankInformationSchema],
    );

    type FormData = yup.InferType<typeof schema>;
    const {
        control, handleSubmit, reset, watch, trigger, setValue, formState: { errors }, setError, clearErrors,
    } = useForm<FormData>({
        resolver: useYupValidationResolver(schema),
        defaultValues: { ...bankInformationData },
    });

    const formData = watch();

    const getSortedBeneficiaryOptions = useCallback(({ bankAccountOption }) => {
        const beneficiaries = beneficiaryInformationFromAPI?.beneficiaries?.map(beneficiary => ({
            id: beneficiary?.id,
            beneficiaryType: beneficiary?.beneficiaryType,
            firstName: beneficiary?.firstName,
            lastName: beneficiary?.lastName,
            address: beneficiary?.address,
            zipCodeOfBeneficiary: beneficiary?.zipCode,
            cityOfBeneficiary: beneficiary?.city,
        }));

        if (bankAccountOption === BankInformationEnum.Value5701) {
            return [beneficiaryInfoStatic];
        }

        if (isPOA && !isPA) {
            if (bankAccountOption === BankInformationEnum.Value5700) {
                return beneficiaries?.filter(user => [CaseBeneficiaryTypeEnum.PowerOfAttorney, CaseBeneficiaryTypeEnum.Owner]?.includes(user?.beneficiaryType));
            }
            if (bankAccountOption === BankInformationEnum.Value5702) {
                return beneficiaries?.filter(user => [CaseBeneficiaryTypeEnum.Owner]?.includes(user?.beneficiaryType));
            }
        }

        if (isJA && !isPA) {
            if (bankAccountOption === BankInformationEnum.Value5701) {
                return [beneficiaryInfoStatic];
            }
            if (bankAccountOption === BankInformationEnum.Value5702) {
                return beneficiaries?.filter(user => user?.beneficiaryType === CaseBeneficiaryTypeEnum.JointAccountCombined);
            }

            return beneficiaries;
        }

        return beneficiaries?.filter(user => user?.beneficiaryType === CaseBeneficiaryTypeEnum.Owner);
    }, [beneficiaryInformationFromAPI?.beneficiaries, isPOA, isJA, isPA, beneficiaryInfoStatic]);

    const getBeneficiaryType = useCallback((beneficiaryType: CaseBeneficiaryTypeEnum) => {
        const prefix = ' - ';

        switch (beneficiaryType) {
            case CaseBeneficiaryTypeEnum.Owner:
                return `${prefix}${t('CaseManagementPage.Modal.PoAModal.AccountOwner')}`;
            case CaseBeneficiaryTypeEnum.PowerOfAttorney:
                return `${prefix}${t('CaseManagementPage.Modal.PoAModal.PowerOfAttorney')}`;
            case CaseBeneficiaryTypeEnum.ParentalAuthority:
                return `${prefix}${t('clientDashboard.portfolios.parentalAuthority')}`;
            case CaseBeneficiaryTypeEnum.BeneficialOwner:
                return `${prefix}${t('3b.kyc.beneficialOwner')}`;
            case CaseBeneficiaryTypeEnum.JointAccountCombined:
                return `${prefix}${t('connections.addConnection.jointAccount')}`;
            default:
                return '';
        }
    }, [t]);

    const sortedBeneficiaries = getSortedBeneficiaryOptions({ bankAccountOption: formData?.bankAccountOption });

    const sortedBeneficiaryOptions = sortedBeneficiaries?.map(beneficiary => ({
        label: `${beneficiary?.firstName} ${beneficiary?.beneficiaryType as number === CaseBeneficiaryTypeEnum.JointAccountCombined ? 'and' : ''} ${beneficiary?.lastName} (${beneficiary?.address}, ${beneficiary?.zipCodeOfBeneficiary} ${beneficiary?.cityOfBeneficiary})${getBeneficiaryType(beneficiary?.beneficiaryType)}`,
        value: beneficiary?.id,
    }));

    const sortedBankAccountOptions = useMemo(
        () => bankInformationOptions.filter(({ value }) => (
            hasAdditional3b || value !== BankInformationEnum.Value5702)),
        [isPA, hasAdditional3b, bankInformationOptions],
    );

    const showAllFieldsForKuDi = useMemo(() => {
        if (formData?.bankAccountOption === BankInformationEnum.Value5700 && isKuDi) {
            return true;
        }

        return false;
    }, [isKuDi, formData?.bankAccountOption]);

    const accountDetailsIban = useMemo(() => active3bPortfolios?.find(item => +item?.portfolioId === +formData?.zurichInvestDepotAccount)?.iban, [active3bPortfolioOptions, formData?.zurichInvestDepotAccount]);

    const onBankAccountChange = useCallback((bankAccountOption: BankInformationEnum) => {
        const shouldRemoveAccountInfo = bankAccountOption === BankInformationEnum.Value5700;

        const sortedOptions = getSortedBeneficiaryOptions({ bankAccountOption });
        const conditionalAccountDetails = bankAccountOption === BankInformationEnum.Value5702 ? accountDetailsZurichDepots : accountDetails;

        reset({
            ...(sortedOptions?.[0] || {}),
            ...conditionalAccountDetails,
            bankAccountOption,
            ...(shouldRemoveAccountInfo && {
                bankName: '',
                zipCode: '',
                city: '',
            }),
            iban: '',
            esr: '',
            zurichInvestDepotAccount: null,
            beneficiaryOption: sortedOptions?.length === 1 ? sortedOptions?.[0]?.id : null,
            id: null,
            messageText1: '',
            messageText2: '',
        });
    }, [accountDetails, accountDetailsZurichDepots, getSortedBeneficiaryOptions]);

    const onActive3bAccountChange = useCallback((zurichInvestDepotAccount: string) => {
        setValue('zurichInvestDepotAccount', zurichInvestDepotAccount);
        setValue('iban', active3bPortfolios?.find(item => +item?.portfolioId === +zurichInvestDepotAccount)?.iban);

        clearErrors(['zurichInvestDepotAccount', 'iban']);
    }, [active3bPortfolios, active3bPortfolioOptions]);

    const onBeneficiaryChange = useCallback((value) => {
        const beneficiaryInfo = sortedBeneficiaries?.find(ben => +ben?.id === +value);

        reset({
            ...formData,
            ...beneficiaryInfo,
            beneficiaryOption: +value,
        });
    }, [formData, sortedBeneficiaries]);

    useEffect(() => {
        if (formData?.bankAccountOption === BankInformationEnum.Value5701 || (isJA && !isPA && formData?.bankAccountOption === BankInformationEnum.Value5701)) {
            reset({
                ...formData,
                ...beneficiaryInfoStatic,
            });
        }
    }, [language]);

    const validateIBan = useCallback(async ({ value }) => {
        const validationError = await validateIban(value);

        if (validationError) {
            setError('iban', {
                message: await validateIban(value) as string,
            });

            return validationError;
        }
        clearErrors('iban');

        return null;
    }, [validateIban]);

    const handleIBanOnBlur = useCallback((e) => {
        validateIBan({ value: e?.target?.value });
    }, [validateIBan]);

    const onSubmit = useCallback(async (data: FormData) => {
        // setContinueButtonLoading(true);
        let validationError: string | null = null;

        console.log(formData?.bankAccountOption);
        if (data?.iban && (formData?.bankAccountOption !== BankInformationEnum.Value5702)) {
            validationError = await validateIBan({ value: data?.iban });
        }

        if (!validationError) {
            saveCaseManagementData({
                bankInformationData: data,
            });
            setStepComplete(true);
        }
        setContinueButtonLoading(false);
    }, [validateIBan, formData?.bankAccountOption]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: handleSubmit(onSubmit),
        isContinueDisabled: !formData?.bankAccountOption || Object.entries(errors)?.length > 0,
        isContinueLoading: continueButtonLoading,
        reSubscriptionStates: [formData?.bankAccountOption],
    });

    return {
        form: {
            control, data: formData, errors, clearErrors, trigger,
        },
        bankAccountOptions: sortedBankAccountOptions,
        active3bPortfolios: active3bPortfolioOptions,
        beneficiaryOptions: sortedBeneficiaryOptions,
        beneficiaryInfoStatic,
        isKuDi,
        showAllFieldsForKuDi,
        isJA,
        isPOA,
        isPA,
        isPK,
        isAzp,
        isActive3bPortfoliosLoading,
        accountDetailsIban,
        accountDetails,
        accountDetailsZurichDepots,
        isPaymentOrderPrivateAccount,
        onBankAccountChange,
        onActive3bAccountChange,
        onBeneficiaryChange,
        handleIBanOnBlur,
    };
};

export default useBankInformation;
